/**
 * Recruitment Phase 2 — Tasks, Assessments, Referrals, Talent Pool, Activity Logs.
 *
 * Bundled into a single file as five small page components. Each page calls
 * the matching endpoint in `recruitment_phase2.py`. Lean by design: list +
 * inline "Add" form + delete, no detail/edit screens yet.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatError } from "../api";
import {
  Phone, Mail, Repeat, PhoneCall, CalendarClock, Plus, Trash2, ClipboardList,
  BookOpen, Users2, Activity, BadgeCheck, ListChecks, FilePlus,
} from "lucide-react";

const fmtDate = (iso) => iso ? String(iso).slice(0, 16).replace("T", " ") : "—";

function Stat({ label, value }) {
  return (
    <span className="text-xs text-muted">{label}: <b className="text-default">{value}</b></span>
  );
}

function emptyTable(colSpan, msg) {
  return <tr><td colSpan={colSpan} className="text-muted">{msg}</td></tr>;
}

/* ──────────────────────────── TASKS ──────────────────────────────────── */

const TASK_TABS = [
  { value: "all",         label: "All",         icon: ClipboardList },
  { value: "call",        label: "Call",        icon: Phone        },
  { value: "email",       label: "Email",       icon: Mail         },
  { value: "followup",    label: "FollowUp",    icon: Repeat       },
  { value: "phonescreen", label: "PhoneScreen", icon: PhoneCall    },
  { value: "interview",   label: "Interview",   icon: CalendarClock},
];

export function CompanyTasks() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("all");
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/recruitment/tasks", { params: { category: tab, status: "open" } }); setRows(r.data); }
    catch (e) { setErr(formatError(e)); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const close = async (id) => {
    try { await api.patch(`/recruitment/tasks/${id}/status?status=closed`); load(); }
    catch (e) { setErr(formatError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/recruitment/tasks/${id}`); load(); }
    catch (e) { setErr(formatError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow flex items-center gap-2"><ListChecks size={12} /> Tasks</span>
          <h2 className="font-display font-black text-2xl mt-1">My recruitment tasks</h2>
        </div>
        <div className="flex items-center gap-3">
          <Stat label="Total Records" value={rows?.length ?? 0} />
          <button className="btn-primary" onClick={() => setShowAdd(true)} data-testid="task-add-btn"><Plus size={14}/> Add New</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TASK_TABS.map((t) => {
          const Icon = t.icon, active = tab === t.value;
          return (
            <button key={t.value} onClick={() => setTab(t.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${active ? "bg-signal text-white" : "bg-paper border text-muted hover:text-signal"}`}
              style={{ borderColor: "var(--rule)" }} data-testid={`task-tab-${t.value}`}>
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {err && <div className="card card-pad text-red-600">{err}</div>}

      <div className="card overflow-x-auto">
        <table className="dense">
          <thead><tr><th>Date</th><th>Category</th><th>Applicant</th><th>Job</th><th>Task Name</th><th>Notes</th><th></th></tr></thead>
          <tbody data-testid="tasks-tbody">
            {rows === null ? emptyTable(7, "Loading…")
              : rows.length === 0 ? emptyTable(7, "No tasks. Click Add New to create one.")
              : rows.map((r) => (
                <tr key={r.id} data-testid={`task-row-${r.id}`}>
                  <td className="text-muted text-xs">{fmtDate(r.task_date)}</td>
                  <td><span className="badge">{r.category}{r.sub_category ? ` · ${r.sub_category}` : ""}</span></td>
                  <td>
                    <div className="font-semibold text-signal">{r.applicant_name}</div>
                    <div className="text-xs text-muted">{r.applicant_email || r.applicant_phone || ""}</div>
                  </td>
                  <td>{r.job_title || "—"}{r.job_code ? <span className="text-xs text-muted"> ({r.job_code})</span> : null}</td>
                  <td>{r.task_name}</td>
                  <td className="text-xs text-muted max-w-[200px] truncate" title={r.notes}>{r.notes || "—"}</td>
                  <td>
                    <button onClick={() => close(r.id)} className="text-emerald-600 hover:text-emerald-800 mr-2" title="Close" data-testid={`task-close-${r.id}`}><BadgeCheck size={14}/></button>
                    <button onClick={() => del(r.id)} className="text-red-500 hover:text-red-700" title="Delete" data-testid={`task-delete-${r.id}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddTaskDialog onClose={() => setShowAdd(false)} onCreated={load} />}
    </div>
  );
}

function AddTaskDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ category: "call", sub_category: "", applicant_name: "", applicant_email: "", job_title: "", task_name: "", task_date: "", notes: "" });
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try { await api.post("/recruitment/tasks", f); onCreated?.(); onClose(); }
    catch (e2) { setMsg(formatError(e2)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 space-y-3" data-testid="add-task-dialog">
        <h3 className="font-display font-black text-xl">Add Task</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="eyebrow">Category</label>
            <select className="input mt-1" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="task-category">
              {TASK_TABS.filter(t=>t.value!=="all").map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select></div>
          <div><label className="eyebrow">Sub-category</label>
            <input className="input mt-1" placeholder="Telephonic / Face to Face" value={f.sub_category} onChange={(e) => set("sub_category", e.target.value)} data-testid="task-subcategory" /></div>
        </div>
        <div><label className="eyebrow">Applicant name *</label>
          <input required className="input mt-1" value={f.applicant_name} onChange={(e) => set("applicant_name", e.target.value)} data-testid="task-applicant-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="eyebrow">Email</label>
            <input type="email" className="input mt-1" value={f.applicant_email} onChange={(e) => set("applicant_email", e.target.value)} data-testid="task-applicant-email" /></div>
          <div><label className="eyebrow">Job title</label>
            <input className="input mt-1" value={f.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="task-job-title" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="eyebrow">Task name *</label>
            <input required className="input mt-1" placeholder="Interview / Screening call" value={f.task_name} onChange={(e) => set("task_name", e.target.value)} data-testid="task-name-input" /></div>
          <div><label className="eyebrow">Task date</label>
            <input type="datetime-local" className="input mt-1" value={f.task_date} onChange={(e) => set("task_date", e.target.value)} data-testid="task-date" /></div>
        </div>
        <div><label className="eyebrow">Notes</label>
          <textarea className="input mt-1 min-h-[70px]" value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="task-notes" /></div>
        {msg && <div className="text-sm text-red-600">{msg}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="task-submit">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

/* ──────────────────────────── ASSESSMENTS ─────────────────────────────── */

export function CompanyAssessments() {
  const [groups, setGroups] = useState(null);
  const [err, setErr] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    try { const r = await api.get("/assessments/groups"); setGroups(r.data); }
    catch (e) { setErr(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id) => { try { await api.patch(`/assessments/groups/${id}/toggle`); load(); } catch (e) { setErr(formatError(e)); } };
  const del = async (id) => { if (!window.confirm("Delete this group?")) return; try { await api.delete(`/assessments/groups/${id}`); load(); } catch (e) { setErr(formatError(e)); } };

  return (
    <div className="space-y-5" data-testid="assessments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow flex items-center gap-2"><BookOpen size={12} /> Assessments</span>
          <h2 className="font-display font-black text-2xl mt-1">Question Groups</h2>
          <p className="text-muted text-sm">Group questions by topic — use them in interview templates or pre-screening forms.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd(true)} data-testid="group-add-btn"><Plus size={14}/> Add New</button>
      </div>
      {err && <div className="card card-pad text-red-600">{err}</div>}
      <div className="card overflow-x-auto">
        <table className="dense">
          <thead><tr><th>#</th><th>Code</th><th>Name</th><th>Description</th><th>Questions</th><th>Default</th><th>Active</th><th></th></tr></thead>
          <tbody data-testid="groups-tbody">
            {groups === null ? emptyTable(8, "Loading…")
              : groups.length === 0 ? emptyTable(8, "No question groups yet.")
              : groups.map((g, i) => (
                <tr key={g.id} data-testid={`group-row-${g.group_code}`}>
                  <td className="font-mono text-xs">{i + 1}</td>
                  <td className="font-semibold">{g.group_code}</td>
                  <td>{g.group_name}</td>
                  <td className="text-muted text-xs">{g.description || "—"}</td>
                  <td><span className="badge" style={{background:'#22c55e1a', color:'#22c55e'}}>{g.question_count ?? 0}</span></td>
                  <td>{g.is_default ? <span className="text-emerald-600">✓</span> : <span className="text-red-500">✗</span>}</td>
                  <td>
                    <button onClick={() => toggle(g.id)} className={`px-2 py-1 rounded text-xs font-bold ${g.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`group-toggle-${g.group_code}`}>
                      {g.active ? "Active" : "Off"}
                    </button>
                  </td>
                  <td>
                    <button onClick={() => del(g.id)} className="text-red-500" data-testid={`group-delete-${g.group_code}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddGroupDialog onClose={() => setShowAdd(false)} onCreated={load} />}
    </div>
  );
}

function AddGroupDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ group_code: "", group_name: "", description: "", is_default: false, active: true });
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try { await api.post("/assessments/groups", f); onCreated?.(); onClose(); }
    catch (e2) { setMsg(formatError(e2)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-3" data-testid="add-group-dialog">
        <h3 className="font-display font-black text-xl">New Question Group</h3>
        <div><label className="eyebrow">Group code *</label>
          <input required className="input mt-1" placeholder="TECH_01" value={f.group_code} onChange={(e) => set("group_code", e.target.value)} data-testid="group-code" /></div>
        <div><label className="eyebrow">Group name *</label>
          <input required className="input mt-1" placeholder="Technical aptitude" value={f.group_name} onChange={(e) => set("group_name", e.target.value)} data-testid="group-name" /></div>
        <div><label className="eyebrow">Description</label>
          <textarea className="input mt-1 min-h-[70px]" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="group-desc" /></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_default} onChange={(e) => set("is_default", e.target.checked)} data-testid="group-default" /> Mark as default</label>
        {msg && <div className="text-sm text-red-600">{msg}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="group-submit">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

/* ──────────────────────────── REFERRALS ──────────────────────────────── */

export function CompanyReferrals() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => { try { const r = await api.get("/recruitment/referrals"); setRows(r.data); } catch (e) { setErr(formatError(e)); } };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/recruitment/referrals/${id}`); load(); } catch (e) { setErr(formatError(e)); } };

  return (
    <div className="space-y-5" data-testid="referrals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow flex items-center gap-2"><FilePlus size={12} /> Referrals</span>
          <h2 className="font-display font-black text-2xl mt-1">Employee referrals</h2>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd(true)} data-testid="referral-add-btn"><Plus size={14}/> Add Referral</button>
      </div>
      {err && <div className="card card-pad text-red-600">{err}</div>}
      <div className="card overflow-x-auto">
        <table className="dense">
          <thead><tr><th>Candidate</th><th>Contact</th><th>Referred For</th><th>Relationship</th><th>Status</th><th>Date</th><th></th></tr></thead>
          <tbody data-testid="referrals-tbody">
            {rows === null ? emptyTable(7, "Loading…")
              : rows.length === 0 ? emptyTable(7, "No referrals yet.")
              : rows.map((r) => (
                <tr key={r.id} data-testid={`referral-row-${r.id}`}>
                  <td className="font-semibold">{r.candidate_name}</td>
                  <td className="text-xs text-muted">{r.candidate_email}{r.candidate_phone ? ` · ${r.candidate_phone}` : ""}</td>
                  <td>{r.referred_for || "—"}</td>
                  <td className="text-xs text-muted">{r.relationship || "—"}</td>
                  <td><span className="badge">{r.status}</span></td>
                  <td className="text-muted text-xs">{String(r.created_at).slice(0,10)}</td>
                  <td><button onClick={() => del(r.id)} className="text-red-500" data-testid={`referral-delete-${r.id}`}><Trash2 size={14}/></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddReferralDialog onClose={() => setShowAdd(false)} onCreated={load} />}
    </div>
  );
}

function AddReferralDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ candidate_name: "", candidate_email: "", candidate_phone: "", referred_for: "", relationship: "", notes: "" });
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => { e.preventDefault(); setBusy(true); setMsg("");
    try { await api.post("/recruitment/referrals", f); onCreated?.(); onClose(); }
    catch (e2) { setMsg(formatError(e2)); } finally { setBusy(false); } };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-3" data-testid="add-referral-dialog">
        <h3 className="font-display font-black text-xl">Add Referral</h3>
        <div><label className="eyebrow">Candidate name *</label>
          <input required className="input mt-1" value={f.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} data-testid="ref-name" /></div>
        <div><label className="eyebrow">Email *</label>
          <input type="email" required className="input mt-1" value={f.candidate_email} onChange={(e) => set("candidate_email", e.target.value)} data-testid="ref-email" /></div>
        <div><label className="eyebrow">Phone</label>
          <input className="input mt-1" value={f.candidate_phone} onChange={(e) => set("candidate_phone", e.target.value)} data-testid="ref-phone" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="eyebrow">Referred for</label>
            <input className="input mt-1" placeholder="Job title" value={f.referred_for} onChange={(e) => set("referred_for", e.target.value)} data-testid="ref-for" /></div>
          <div><label className="eyebrow">Relationship</label>
            <input className="input mt-1" placeholder="Ex-colleague / friend" value={f.relationship} onChange={(e) => set("relationship", e.target.value)} data-testid="ref-rel" /></div>
        </div>
        {msg && <div className="text-sm text-red-600">{msg}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="ref-submit">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

/* ──────────────────────────── TALENT POOL ────────────────────────────── */

export function CompanyTalentPool() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const load = async () => { try { const r = await api.get("/recruitment/talent-pool"); setRows(r.data); } catch (e) { setErr(formatError(e)); } };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Remove?")) return; try { await api.delete(`/recruitment/talent-pool/${id}`); load(); } catch (e) { setErr(formatError(e)); } };
  return (
    <div className="space-y-5" data-testid="talent-pool-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow flex items-center gap-2"><Users2 size={12} /> Talent pool</span>
          <h2 className="font-display font-black text-2xl mt-1">Saved candidates</h2>
          <p className="text-muted text-sm">Manually-curated bench you can pull from anytime.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowAdd(true)} data-testid="talent-add-btn"><Plus size={14}/> Add Candidate</button>
      </div>
      {err && <div className="card card-pad text-red-600">{err}</div>}
      <div className="card overflow-x-auto">
        <table className="dense">
          <thead><tr><th>Name</th><th>Contact</th><th>Skills</th><th>Exp</th><th>Source</th><th></th></tr></thead>
          <tbody data-testid="talent-tbody">
            {rows === null ? emptyTable(6, "Loading…")
              : rows.length === 0 ? emptyTable(6, "Empty pool. Click Add Candidate to start.")
              : rows.map((r) => (
                <tr key={r.id} data-testid={`talent-row-${r.id}`}>
                  <td className="font-semibold">{r.name}</td>
                  <td className="text-xs text-muted">{r.email}{r.phone ? ` · ${r.phone}` : ""}</td>
                  <td className="text-xs">{(r.skills || []).slice(0, 4).join(", ") || "—"}</td>
                  <td>{r.experience_years ?? "—"} yrs</td>
                  <td className="text-xs"><span className="badge">{r.source}</span></td>
                  <td><button onClick={() => del(r.id)} className="text-red-500" data-testid={`talent-delete-${r.id}`}><Trash2 size={14}/></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {showAdd && <AddTalentDialog onClose={() => setShowAdd(false)} onCreated={load} />}
    </div>
  );
}

function AddTalentDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", skills: "", experience_years: "", current_role: "", source: "manual", notes: "" });
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => { e.preventDefault(); setBusy(true); setMsg("");
    try {
      await api.post("/recruitment/talent-pool", {
        ...f,
        skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
        experience_years: f.experience_years === "" ? null : Number(f.experience_years),
      });
      onCreated?.(); onClose();
    } catch (e2) { setMsg(formatError(e2)); } finally { setBusy(false); } };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-3" data-testid="add-talent-dialog">
        <h3 className="font-display font-black text-xl">Add to Talent Pool</h3>
        <div><label className="eyebrow">Name *</label>
          <input required className="input mt-1" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="talent-name" /></div>
        <div><label className="eyebrow">Email *</label>
          <input required type="email" className="input mt-1" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="talent-email" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="eyebrow">Phone</label>
            <input className="input mt-1" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="talent-phone" /></div>
          <div><label className="eyebrow">Experience (yrs)</label>
            <input type="number" min="0" step="0.5" className="input mt-1" value={f.experience_years} onChange={(e) => set("experience_years", e.target.value)} data-testid="talent-exp" /></div>
        </div>
        <div><label className="eyebrow">Skills (comma-separated)</label>
          <input className="input mt-1" placeholder="Python, AWS, Kubernetes" value={f.skills} onChange={(e) => set("skills", e.target.value)} data-testid="talent-skills" /></div>
        <div><label className="eyebrow">Source</label>
          <select className="input mt-1" value={f.source} onChange={(e) => set("source", e.target.value)} data-testid="talent-source">
            <option value="manual">Manual</option><option value="linkedin">LinkedIn</option><option value="referral">Referral</option>
          </select></div>
        {msg && <div className="text-sm text-red-600">{msg}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="talent-submit">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

/* ──────────────────────────── ACTIVITY LOGS ──────────────────────────── */

export function CompanyActivityLogs() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/recruitment/activity-logs").then((r) => setRows(r.data)).catch((e) => setErr(formatError(e))); }, []);
  return (
    <div className="space-y-5" data-testid="activity-logs-page">
      <div>
        <span className="eyebrow flex items-center gap-2"><Activity size={12} /> Activity logs</span>
        <h2 className="font-display font-black text-2xl mt-1">Recruiter audit trail</h2>
        <p className="text-muted text-sm">Every action you take on this company account, sorted newest first.</p>
      </div>
      {err && <div className="card card-pad text-red-600">{err}</div>}
      <div className="card overflow-x-auto">
        <table className="dense">
          <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Target</th><th>Details</th></tr></thead>
          <tbody data-testid="activity-tbody">
            {rows === null ? emptyTable(5, "Loading…")
              : rows.length === 0 ? emptyTable(5, "No activity yet — your actions will appear here.")
              : rows.map((r) => (
                <tr key={r.id} data-testid={`activity-row-${r.id}`}>
                  <td className="text-xs text-muted">{fmtDate(r.created_at)}</td>
                  <td className="font-semibold">{r.actor_name}</td>
                  <td><span className="badge">{r.action}</span></td>
                  <td className="text-xs">{r.target_type}{r.target_id ? ` · ${r.target_id.slice(0, 8)}` : ""}</td>
                  <td className="text-xs text-muted max-w-[260px] truncate" title={JSON.stringify(r.meta)}>{r.meta ? JSON.stringify(r.meta) : "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
